import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap/dist/gsap';
import { Draggable } from 'gsap/dist/Draggable';
import { InertiaPlugin } from 'gsap/dist/InertiaPlugin';

import style from './ProjectSelectorCarousel.module.scss';
import { ProjectType } from 'pages/project/[id]';
import { ImageBlock } from 'components/general/imageBlock/ImageBlock';
import { isIOS, isMobileWidth } from 'utils/helpers';
import { useWindowSize } from 'hooks/useWindowSize';
import { VideoJsPlayerOptions } from 'video.js';
import { VideoJSPlayer } from 'components/general/videoBlock/VideoJSPlayer';

if (typeof window !== 'undefined') {
  // Due to SSR we can only register when we are in the browser
  gsap.registerPlugin(Draggable, InertiaPlugin);
}

export type ProjectSelectorCarouselType = {
  // this will be the first slide as we re-order the projects
  activeProjectSlug: string;
  //use this to go to a slide
  selectedProjectSlide: string;
  projects: ProjectType[];
  onProjectSelect: (p: ProjectType) => void;
};
export const ProjectSelectorCarousel = ({
  activeProjectSlug,
  projects,
  onProjectSelect,
  selectedProjectSlide,
}: ProjectSelectorCarouselType): JSX.Element => {
  const slider = useRef<HTMLDivElement>(null);
  const bounds = useRef<HTMLDivElement>(null);
  const metaContainer = useRef<HTMLDivElement>(null);
  const draggableRef = useRef();
  const scrollBarWidthRef = useRef<number>(0);

  const [orderedProjects, setOrderedProjects] = useState<ProjectType[]>([]);

  const currentProjectIndex = projects.findIndex((p) => p.slug === activeProjectSlug);
  const currentProject = projects[currentProjectIndex];

  const size = useWindowSize();
  const isPhone = isMobileWidth(size.width);

  const update = (reset: boolean = false, forceDraggableUpdate: boolean = false) => {
    if (!draggableRef.current) return;

    // @ts-ignore
    forceDraggableUpdate && draggableRef.current.update();

    const draggable: any = draggableRef.current;
    const slideWidth = isMobileWidth(window.innerWidth) ? window.innerWidth : window.innerWidth / 2;
    const center = isMobileWidth(window.innerWidth) ? 0 : window.innerWidth / 2;

    // InertiaPlugin.track(slider.current, 'x');

    if (metaContainer.current) {
      const metaItems = gsap.utils.toArray(metaContainer.current.children);
      const progressCenter = reset ? 0 : (draggable.x - center) / -slideWidth;
      const indexAtCenter = Math.floor(progressCenter);
      const indexAtCenterNext = indexAtCenter + 1;
      const metaItemCurrent: any = metaItems[indexAtCenter];
      const metaItemNext: any = metaItems[indexAtCenterNext];
      const progressOfSlideAtCenter = progressCenter - indexAtCenter;
      // between -1 and 0
      const progressOfNextSlideAtCenter = progressCenter - indexAtCenterNext;
      const PI = Math.PI;
      // TODO: ideally we get the bounding rect, this is only a temp number
      const offsetCurrent = 300;
      const offsetNext = 200;
      const pendulumCurrent = Math.cos(progressOfSlideAtCenter * PI);
      const pendulumNext = Math.cos(progressOfNextSlideAtCenter * PI);

      const xCurrent = -offsetCurrent + pendulumCurrent * offsetCurrent;
      // offset 50% of the current
      // const xNext = -offset + Math.sin(progressOfSlideAtCenter * (PI / 2)) * offset;
      const xNext = -offsetNext + pendulumNext * offsetNext;

      gsap.set(metaItems, { alpha: 0 });

      if (reset) {
        // we have an empty cell, so index starts at 1
        const currentItem: any = metaItems[1];
        const nextItem: any = metaItems[2];
        gsap.set(currentItem, { x: 0, alpha: 1 });
        gsap.set(nextItem, { x: -offsetNext, alpha: 0 });
      } else {
        gsap.set(metaItemCurrent, { x: `${xCurrent}`, alpha: pendulumCurrent });
        gsap.set(metaItemNext, { x: `${xNext}`, alpha: pendulumNext });
      }

      // const currentVelocity = InertiaPlugin.getVelocity(slider.current, 'x');
      // const posVelocity = Math.abs(currentVelocity);
      // const speedThreshold = 100;
      // if (posVelocity < speedThreshold) {
      //   //show the label and title
      // } else {
      //   //hide the label and title
      // }
    }
  };

  const onWindowResize = () => {
    if (!draggableRef.current) return;
    const draggable: any = draggableRef.current;
    const isPhone = isMobileWidth(window.innerWidth);
    if (isPhone) {
      gsap.killTweensOf(draggable.target);
      gsap.set(draggable.target, {
        x: -window.innerWidth,
      });
      update(true);
      draggable.disable();
    } else {
      // gsap.set('.js-project-hero-slide', { width: `${window.innerWidth / 2}px` });
      if (!draggable.enabled()) {
        draggable.enable();
        gsap.set(draggable.target, {
          x: 0,
        });
        update(true);
      }
    }
  };

  useEffect(() => {
    // keep order, but index 0 should be the current project
    // we do this on mount because we can navigate to a new project, but we don't want to change the order  in that case!
    const clonedProjects = [...projects];
    const removed = clonedProjects.splice(0, currentProjectIndex);

    setOrderedProjects([...clonedProjects, ...removed]);

    const scrollDiv = document.createElement('div');
    scrollDiv.style.width = '100px';
    scrollDiv.style.height = '100px';
    scrollDiv.style.overflow = 'scroll';
    scrollDiv.style.position = 'absolute';
    scrollDiv.style.top = '-9999px';

    document.body.appendChild(scrollDiv);

    // Get the scrollbar width
    const scrollbarWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth;
    console.warn(scrollbarWidth);

    // Delete the div
    document.body.removeChild(scrollDiv);

    scrollBarWidthRef.current = scrollbarWidth;
  }, []);

  useEffect(() => {
    // we want to create this when orderedprojects is set
    const draggable = Draggable.create(slider.current, {
      type: 'x',
      bounds: bounds.current,
      inertia: true,
      edgeResistance: 0.95,
      dragResistance: 0,
      throwProps: true,

      callbackScope: this,
      zIndexBoost: false,
      allowContextMenu: false,
      snap: {
        x: (value) => {
          //snap to the closest increment of half the window.
          const snapPoint = window.innerWidth / 2;
          const target = Math.round(value / snapPoint) * snapPoint;
          const index = Math.min(orderedProjects.length - 1, Math.abs(target / snapPoint));
          onProjectSelect(orderedProjects[index]);
          return target - scrollBarWidthRef.current;
        },
      },
      onThrowUpdate: (e) => {
        update();
      },
      onDrag: (e) => {
        update();
      },
    })[0];
    // @ts-ignore
    draggableRef.current = draggable;
    // iOS fires resize events when scrolling, we only want to check for orientation on those specific devices
    if (isIOS()) {
      window.addEventListener('orientationchange', onWindowResize);
    } else {
      window.addEventListener('resize', onWindowResize);
    }
    update();
    onWindowResize();

    return () => {
      draggable.kill();
      window.removeEventListener('resize', onWindowResize);
      window.removeEventListener('orientationchange', onWindowResize);
    };
  }, [orderedProjects]);

  useEffect(() => {
    if (draggableRef.current) {
      const onMobile = isMobileWidth(window.innerWidth);
      // const target = Math.round(value / snapPoint) * snapPoint;
      const snapPoint = onMobile ? window.innerWidth / 1 : window.innerWidth / 2; //- scrollBarWidthRef.current;

      // mobile is full width slides, so we need to counter the empty cell!
      const offset = onMobile ? 1 : 0;

      const index =
        orderedProjects.findIndex((proj) => proj.slug === selectedProjectSlide) + offset;

      if (index > -1) {
        const x = -index * snapPoint - scrollBarWidthRef.current;
        // @ts-ignore
        const tween = draggableRef.current.tween;
        const isTweening = tween && tween.isActive();
        if (!tween || !isTweening) {
          // we need to move it manually
          if (onMobile) {
            //we don't want to see the scroll anmation on mobile, jus a fade. hack it like dis yo
            // @ts-ignore
            gsap.to(draggableRef.current.target, {
              duration: 0.1,
              alpha: 0,
              x,
              // onUpdate: () => () => {
              //   console.log('YYYEYSSS');
              //   update(false, true);
              // },
              onComplete: () => {
                update(false, true);
                // @ts-ignore
                gsap.to(draggableRef.current.target, { alpha: 1, delay: 1 });
              },
            });
          } else {
            // @ts-ignore
            gsap.to(draggableRef.current.target, {
              alpha: 1,
              x,
              // onUpdate: () => () => {
              //   console.log('NEOEOEOEO');
              //   update(false, true);
              // },
              onComplete: () => update(false, true),
            });
          }
        }
      }
    }
  }, [selectedProjectSlide]);

  return (
    <section ref={bounds} className={style.row}>
      <div ref={metaContainer} className={style.sliderMeta}>
        {/* empty slide to make calculations easier :) */}
        <div className={style.metaItem}></div>
        {orderedProjects.map((p, index) => {
          return (
            <div key={`${index}-${p.slug}-label`} className={style.metaItem}>
              <p className={style.metaItemTitle}>{p.tagline}</p>
              <p className={style.metaItemTagline}>{p.full_name}</p>
            </div>
          );
        })}
      </div>
      <div className={style.bg}></div>
      <div className={style.sliderWrapper}>
        <div ref={slider} className={style.slider}>
          <div className={style.heroSlide}>{/* empty slide to make calculations easier :) */}</div>
          {orderedProjects.map((p, index) => {
            const videoJsOptions: VideoJsPlayerOptions = {
              controls: false,
              responsive: true,
              controlBar: false,
              bigPlayButton: false,
              fill: true,
              autoplay: true,
              preload: 'auto',
              loop: true,
              muted: true,
              poster: isPhone && p.hero.phone_image ? p.hero.phone_image : p.hero.image,

              sources: [
                {
                  src: isPhone && p.hero.phone_video ? p.hero.phone_video : p.hero.video!,
                },
              ],
            };

            return (
              <div
                key={`${index}-${p.slug}-img`}
                className={`${style.heroSlide} js-project-hero-slide`}
              >
                {p.hero.video ? (
                  <VideoJSPlayer
                    playsInline={true}
                    options={videoJsOptions}
                    onVideoPlay={() => {}}
                  />
                ) : (
                  <ImageBlock
                    desktop={{
                      src: p.hero.image,
                      layout: 'fill',
                      objectFit: 'cover',
                      loading: 'eager',
                    }}
                    alt={p.hero.alt_text}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
